/**
 * Compare mode — up to 3 side-by-side single-expiration ladders
 * (Heatseeker "Trinity" idiom), Pro-only, embedded in the Options page.
 *
 * Entry: drag a ticker pill / favorite / recent chip onto the drop zone
 * (desktop) or use the "+ Compare" picker (mobile). Each panel opens its
 * own WebSocket (the backend's tier gates enforce per-ticker access) and
 * renders ONE expiration's strike ladder with the full set of heatmap
 * treatments: per-panel color normalization, spot row + white pointer,
 * ★ on the panel's dominant node (violet when short-gamma — the day-low
 * family), FLIP tag, violet most-negative cell.
 *
 * Expiration selection is GLOBAL: a selector lists the FIRST panel's
 * expirations (default FRONT). Tickers have different calendars, so each
 * panel renders its own NEAREST available expiration and shows an
 * "≈ MMM-DD" marker when its actual date differs from the chosen one.
 *
 * Reorder: drag a panel's header over another panel. Remove: the X in
 * each header; dropping below 2 panels exits compare mode entirely.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useWebSocket } from '../../hooks/useWebSocket';
import { apiGet } from '../../lib/api';
import { ViewMode } from '../../lib/types';
import { createCellColorScale, getCellTextColor, formatExposureValue, WALL_PEAK_BAND } from '../../lib/colors';
import { useThemeKey } from '../../hooks/useThemeKey';
import { formatStrike } from '../../lib/format';
import { StrikeWindow } from '../../hooks/useStrikeWindow';

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

function fmtExp(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${MONTHS[parseInt(m[2], 10) - 1]}-${m[3]}`;
}

/** Nearest available expiration (ISO strings) to the chosen ISO date. */
function nearestExpiration(available: string[], chosen: string | null): string | null {
  if (!available.length) return null;
  if (!chosen) return available[0]; // FRONT default — lists are sorted asc
  if (available.includes(chosen)) return chosen;
  const target = Date.parse(chosen);
  let best = available[0];
  let bestDiff = Infinity;
  for (const iso of available) {
    const diff = Math.abs(Date.parse(iso) - target);
    if (diff < bestDiff) { bestDiff = diff; best = iso; }
  }
  return best;
}

// ── Single panel ───────────────────────────────────────────────────────────

interface PanelProps {
  ticker: string;
  chosenExp: string | null;          // null = front
  viewMode: ViewMode;
  window: StrikeWindow;
  onRemove: (ticker: string) => void;
  /** Reorder plumbing: header drag handlers managed by the strip. */
  onHeaderDragStart: (ticker: string) => void;
  onHeaderDrop: (ticker: string) => void;
  /** First panel reports its expiration list up for the global selector. */
  onExpirations?: (isoDates: string[]) => void;
}

const ComparePanel: React.FC<PanelProps> = ({
  ticker, chosenExp, viewMode, window: win,
  onRemove, onHeaderDragStart, onHeaderDrop, onExpirations,
}) => {
  const { latestFrame, isConnected, error } = useWebSocket(ticker);
  const [prevClose, setPrevClose] = useState<number | null>(null);
  const themeKey = useThemeKey();

  // Day-% chip data — slow poll, degrade silently.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const tick = async () => {
      try {
        const d = await apiGet<{ prev_close: number | null }>(`/spot/${ticker}/prev_close`, ctrl.signal);
        if (!cancelled) setPrevClose(d.prev_close);
      } catch { /* chip shows nothing */ }
    };
    tick();
    const id = setInterval(tick, 120_000);
    return () => { cancelled = true; ctrl.abort(); clearInterval(id); };
  }, [ticker]);

  // Report expirations to the strip (only wired for the first panel).
  const byExp = viewMode === 'gex'
    ? latestFrame?.gex_by_expiration
    : latestFrame?.vex_by_expiration;
  const expList = useMemo(() => Object.keys(byExp || {}).sort(), [byExp]);
  useEffect(() => {
    if (onExpirations && expList.length) onExpirations(expList);
  }, [expList, onExpirations]);

  const computed = useMemo(() => {
    if (!latestFrame || !byExp) return null;
    const spot = latestFrame.spot_price;
    const actualExp = nearestExpiration(expList, chosenExp);
    if (!actualExp) return null;
    const col: Record<string, number> = byExp[actualExp] || {};
    let strikes = Object.keys(col).map(parseFloat).filter(s => !isNaN(s)).sort((a, b) => a - b);
    if (!strikes.length) return null;

    // Strike window (shared page control): N strikes each side of spot.
    if (win !== 0) {
      let spotIdx = strikes.findIndex(s => s >= spot);
      if (spotIdx < 0) spotIdx = strikes.length - 1;
      strikes = strikes.slice(Math.max(0, spotIdx - win), Math.min(strikes.length, spotIdx + win + 1));
    }

    const values = strikes.map(s => col[String(s)] ?? col[s.toFixed(1)] ?? 0);
    const maxAbs = Math.max(...values.map(Math.abs), 0);
    const colorFn = createCellColorScale(maxAbs);

    // Per-panel peak |gamma| per sign: largest long (CALL WALL) + largest
    // short (PUT WALL). Cells within WALL_PEAK_BAND of these get the accent
    // hue, matching the main heatmap. King = dominant |value| (★ + chip).
    let kingIdx = 0;
    let maxPos = 0, minNeg = 0;
    values.forEach((v, i) => {
      if (Math.abs(v) > Math.abs(values[kingIdx])) kingIdx = i;
      if (v > maxPos) maxPos = v;
      if (v < minNeg) minNeg = v;
    });
    const _css = getComputedStyle(document.documentElement);
    const posHex = _css.getPropertyValue('--gv-pos').trim() || '#facc15';
    const negHex = _css.getPropertyValue('--gv-heat-neg').trim() || '#760051';

    // Spot row + flip strike (nearest grid strikes).
    let spotIdx = strikes.findIndex(s => s >= spot);
    if (spotIdx < 0) spotIdx = strikes.length - 1;
    const flip = latestFrame.gex_flip_point;
    let flipIdx = -1;
    if (flip != null) {
      let best = Infinity;
      strikes.forEach((s, i) => {
        const d = Math.abs(s - flip);
        if (d < best) { best = d; flipIdx = i; }
      });
      // Only tag the flip when it's actually inside the visible ladder.
      if (flipIdx === 0 || flipIdx === strikes.length - 1) {
        if (best > (strikes[1] - strikes[0]) * 2) flipIdx = -1;
      }
    }

    const kingVal = values[kingIdx];
    return {
      spot, actualExp, strikes, values, colorFn, maxAbs,
      kingStrike: strikes[kingIdx], kingVal, kingIsNeg: kingVal < 0,
      kingDistPct: spot > 0 ? (strikes[kingIdx] - spot) / spot : null,
      maxPos, minNeg, posHex, negHex,
      spotStrike: strikes[spotIdx],
      flipStrike: flipIdx >= 0 ? strikes[flipIdx] : null,
    };
  }, [latestFrame, byExp, expList, chosenExp, win, themeKey]);

  const pct = computed && prevClose ? (computed.spot - prevClose) / prevClose : null;
  const expDiffers = computed && chosenExp && computed.actualExp !== chosenExp;

  return (
    <div className="cmp-panel">
      <div
        className="cmp-head"
        draggable
        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; onHeaderDragStart(ticker); }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); onHeaderDrop(ticker); }}
        title="Drag to reorder"
      >
        <span className="cmp-grip" aria-hidden="true">⋮⋮</span>
        <span className="cmp-sym">{ticker}</span>
        {computed && <span className="cmp-spot">{computed.spot.toFixed(2)}</span>}
        {pct != null && (
          <span className={`cmp-pct ${pct >= 0 ? 'pos' : 'neg'}`}>
            {pct >= 0 ? '+' : ''}{(pct * 100).toFixed(2)}%
          </span>
        )}
        {computed?.kingDistPct != null && (
          <span
            className={`cmp-king-chip${computed.kingIsNeg ? ' neg' : ''}`}
            title={`King ${formatStrike(computed.kingStrike)} — dominant ${viewMode.toUpperCase()} node of this expiry`}
          >
            ♔ {Math.abs(computed.kingDistPct * 100).toFixed(1)}%{computed.kingDistPct >= 0 ? '↑' : '↓'}
          </span>
        )}
        <button
          type="button"
          className="cmp-remove"
          aria-label={`Remove ${ticker} from compare`}
          onClick={() => onRemove(ticker)}
        >×</button>
      </div>
      <div className="cmp-exp-row">
        {computed
          ? <>{expDiffers && <span className="cmp-exp-approx" title="Nearest available expiration for this ticker">≈ </span>}{fmtExp(computed.actualExp)}</>
          : (error ? 'unavailable' : isConnected ? 'loading…' : 'connecting…')}
      </div>
      <div className="cmp-ladder" role="table" aria-label={`${ticker} ${viewMode.toUpperCase()} ladder`}>
        {computed && [...computed.strikes].reverse().map((s, ri) => {
          const i = computed.strikes.length - 1 - ri;
          const v = computed.values[i];
          const isSpot = s === computed.spotStrike;
          const isKing = s === computed.kingStrike;
          const isCallWall = v > 0 && v >= computed.maxPos * (1 - WALL_PEAK_BAND);
          const isPutWall  = v < 0 && v <= computed.minNeg * (1 - WALL_PEAK_BAND);
          const bg = isCallWall ? computed.posHex : isPutWall ? computed.negHex : computed.colorFn(v);
          const fg = getCellTextColor(v, computed.maxAbs, bg);
          return (
            <div
              key={s}
              className={[
                'cmp-row',
                isSpot ? 'is-spot' : '',
                isKing ? (computed.kingIsNeg ? 'is-king-neg' : 'is-king') : '',
                s === computed.flipStrike ? 'is-flip' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className="cmp-strike">{formatStrike(s)}</span>
              <span className={`cmp-val${isCallWall ? ' is-call-wall' : ''}${isPutWall ? ' is-put-wall' : ''}`} style={{ backgroundColor: bg, color: fg }}>
                {Math.abs(v) > 0.5 ? formatExposureValue(v) : ''}
                {isKing && <span className="cmp-star" aria-hidden="true">★</span>}
              </span>
            </div>
          );
        })}
        {!computed && <div className="cmp-empty">{error ? 'No access or no data for this ticker.' : 'Waiting for data…'}</div>}
      </div>
    </div>
  );
};

// ── Strip (the side-by-side container) ─────────────────────────────────────

interface StripProps {
  tickers: string[];                       // 2..3, render order
  viewMode: ViewMode;
  window: StrikeWindow;
  onRemove: (ticker: string) => void;
  onReorder: (next: string[]) => void;
}

const ComparePanels: React.FC<StripProps> = ({ tickers, viewMode, window: win, onRemove, onReorder }) => {
  const [chosenExp, setChosenExp] = useState<string | null>(null); // null = FRONT
  const [expOptions, setExpOptions] = useState<string[]>([]);
  const [dragSym, setDragSym] = useState<string | null>(null);

  const headerDrop = (target: string) => {
    if (!dragSym || dragSym === target) return;
    const next = [...tickers];
    const from = next.indexOf(dragSym);
    const to = next.indexOf(target);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, dragSym);
    onReorder(next);
    setDragSym(null);
  };

  return (
    <div className="cmp-wrap">
      <div className="cmp-toolbar">
        <span className="cmp-toolbar-label">COMPARE · {viewMode.toUpperCase()}</span>
        <label className="cmp-exp-label">
          Expiration
          <select
            className="cmp-exp-select"
            value={chosenExp ?? ''}
            onChange={e => setChosenExp(e.target.value || null)}
          >
            <option value="">FRONT</option>
            {expOptions.map(iso => (
              <option key={iso} value={iso}>{fmtExp(iso)}</option>
            ))}
          </select>
        </label>
        <span className="cmp-hint">drag headers to reorder · × to remove</span>
      </div>
      <div className="cmp-grid" data-count={tickers.length}>
        {tickers.map((t, i) => (
          <ComparePanel
            key={t}
            ticker={t}
            chosenExp={chosenExp}
            viewMode={viewMode}
            window={win}
            onRemove={onRemove}
            onHeaderDragStart={setDragSym}
            onHeaderDrop={headerDrop}
            onExpirations={i === 0 ? setExpOptions : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default ComparePanels;
